import { Injectable } from '@angular/core';
import { unified } from 'unified';
import remarkParse from 'remark-parse';
import remarkGfm from 'remark-gfm';
import supersub from 'remark-supersub';
import remarkRehype from 'remark-rehype';
import rehypeSanitize from 'rehype-sanitize';
import rehypeHighlight from 'rehype-highlight';
import rehypeExternalLinks from 'rehype-external-links';
import rehypeStringify from 'rehype-stringify';
import DOMPurify from 'dompurify';
import { XSSAllowTags } from '../ai-chat.constant';

@Injectable({
  providedIn: 'root'
})
export class MarkdownService {
  // Initialize the markdown processor with a series of plugins to parse, transform, and sanitize markdown content.
  // - remarkParse: Parses the markdown text into a syntax tree.
  // - remarkGfm: Adds support for GitHub Flavored Markdown (GFM) features like tables, strikethrough, etc.
  // - supersub: Adds support for superscript and subscript syntax.
  // - remarkRehype: Transforms the markdown syntax tree to a HTML syntax tree.
  // - .data('settings', { fragment: true } as any): Configures the processor to handle fragments of HTML.
  // - rehypeSanitize: Sanitizes the HTML to prevent XSS attacks.
  // - rehypeHighlight: Adds syntax highlighting to code blocks, with automatic language detection.
  // - rehypeExternalLinks: Adds attributes to external links, such as 'nofollow' and opening in a new tab.
  // - rehypeStringify: Converts the HTML syntax tree to a string.
  private markdownProcessor = unified()
    .use(remarkParse)
    .use(remarkGfm)
    .use(supersub)
    .use(remarkRehype)
    .data('settings', { fragment: true } as any) // Use type assertion to bypass type checking
    // TODO: Add the link when the business rules have been created.  They have been removed for the time being.
    // .use(rehypeSanitize, {
    //   // Disallow anchor tags
    //   tagNames: XSSAllowTags.filter(tag => tag !== 'a')
    // })
    .use(rehypeSanitize)
    .use(rehypeHighlight, { detect: true })
    .use(rehypeExternalLinks, { rel: ['nofollow'], target: ['_blank'] } as any) // Pass options directly
    .use(rehypeStringify);

  /**
   * Processes the given markdown text and returns sanitized HTML.
   * The markdown text is parsed, transformed, and sanitized to prevent XSS attacks.
   */
  async processMarkdown(markdownText: string): Promise<string> {
    const processedText = await this.markdownProcessor.process(markdownText);

    // Ensure the processedText.value is a string before sanitizing
    let textValue = String(processedText.value);

    return DOMPurify.sanitize(textValue, {
      ALLOWED_TAGS: XSSAllowTags,
      ADD_ATTR: ['target']
    });
  }
}
